// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import axios from "axios";
import Logger from "js-logger";
import { Utils, MediaRecorderInfo } from "@/js/Utils";
import { EventLogger } from "@/js/EventLogger";
import FrameCompare from "@/js/FrameCompare";
import { LivenessGlobalError } from "@/js/ErrorManager";

interface BinaryRequestData {
  readonly timestamp: number;
  readonly token: string;
}
interface FramesRequestData extends BinaryRequestData {
  readonly frameBase64: string;
}

interface VideoRequestData extends BinaryRequestData {
  readonly videoBase64: string;
  readonly extension: string;
}

interface ResponseData {
  readonly message: string;
}

interface VerifyRequestData {
  readonly token: string;
}

interface VerifyResponseData {
  readonly success: boolean;
  readonly logs: string;
}

enum FrameUploadResult {
  Ok = 1,
  Fail = 2
}

const FRAMES_FAILED_RATE_THRESHOLD = 0.1;

export class RemoteVerifier {
  private readonly challengeId: string;
  private readonly token: string;
  private readonly videoElement: HTMLVideoElement;
  private readonly frameUploadPromises: Promise<FrameUploadResult>[];
  private readonly invisibleCanvas: HTMLCanvasElement;
  private previousFrame: Blob | null;
  private isMatchFaceBetweenTwoFrame: boolean;

  constructor(challengeId: string, token: string, videoElement: HTMLVideoElement) {
    this.challengeId = challengeId;
    this.token = token;
    this.videoElement = videoElement;
    this.frameUploadPromises = [];
    this.previousFrame = null;
    this.isMatchFaceBetweenTwoFrame = true;

    // Create canvas to convert video frames to blob
    this.invisibleCanvas = document.createElement("canvas");
    this.invisibleCanvas.width = this.videoElement.width;
    this.invisibleCanvas.height = this.videoElement.height;
  }

  uploadFrame() {
    const context = this.invisibleCanvas.getContext("2d");
    if (context === null) {
      throw "Error getting canvas context";
    }
    context.drawImage(this.videoElement, 0, 0, this.videoElement.width, this.videoElement.height);
    if (Utils.getConfigBooleanValue("FLIP_VIDEO")) {
      context.scale(-1, 1);
    }
    const canvas = this.invisibleCanvas;
    const self = this;
    this.frameUploadPromises.push(
      new Promise(function(resolve: (v: FrameUploadResult) => void, reject: (error: Error) => void) {
        canvas.toBlob(
          async function(blob) {
            if (blob === null) {
              reject(new Error("Error creating blob from canvas"));
              return;
            }

            if (Utils.getConfigBooleanValue("REALTIME_FRAME_COMPARE_ENABLED")) {
              console.log("compare frame!!!");
              if (self.previousFrame) {
                const frameCompare = new FrameCompare(blob, self.previousFrame);
                self.isMatchFaceBetweenTwoFrame = await frameCompare.compare();

                if (!self.isMatchFaceBetweenTwoFrame) {
                  resolve(FrameUploadResult.Fail);

                  return;
                }
              }

              self.previousFrame = blob;
            }

            RemoteVerifier.callFramesApi(
              self.challengeId,
              self.token,
              blob,
              () => resolve(FrameUploadResult.Ok),
              () => resolve(FrameUploadResult.Fail)
            );
          },
          "image/jpeg",
          parseFloat(Utils.getConfig().IMAGE_JPG_QUALITY)
        );
      })
    );
  }

  static callFramesApi(
    challengeId: string,
    token: string,
    blob: Blob,
    resolve: (v: any) => void,
    reject: (error: Error) => void
  ) {
    Logger.info("uploading frame");
    const reader: FileReader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function() {
      const framesEndpoint: string = Utils.getConfig().API_FRAMES_ENDPOINT_PATTERN.replace(
        "{challengeId}",
        challengeId
      );
      const url: string = Utils.getConfig().API_URL + framesEndpoint;

      Logger.info(`calling ${url}`);
      const dataUrl = reader.result as string;
      const requestData: FramesRequestData = {
        timestamp: Date.now(),
        frameBase64: dataUrl.substr(dataUrl.indexOf(",") + 1),
        token: token
      };
      Logger.info(requestData);
      const promise = axios.put(url, requestData);
      promise
        .then(function(response: any) {
          Logger.info(response);
          const verifyResponseData: ResponseData = response.data;
          Logger.info(verifyResponseData);
          Logger.info("frame successfully uploaded");
          resolve(undefined);
        })
        .catch(function(error: any) {
          Logger.error(error);
          reject(error);
        });
    };
  }

  verify(successCallback: (result: boolean, logs: string) => void, errorCallback: (error: Error) => void): void {
    Logger.debug(this.frameUploadPromises);
    const self = this;
    Promise.all(this.frameUploadPromises).then(function(results) {
      if (!self.isMatchFaceBetweenTwoFrame) {
        const error = new LivenessGlobalError("Unexpected error");
        new Promise((_, reject) => setTimeout(() => reject(error), 3000));

        return;
      }

      const totalCount = results.length;
      if (!totalCount) {
        Logger.error("No frame uploaded. Stop verify!!!");
        return;
      }
      const failCount = results.filter(r => r === FrameUploadResult.Fail).length;
      const failedRate = (failCount * 1.0) / totalCount;
      if (failedRate > FRAMES_FAILED_RATE_THRESHOLD) {
        Logger.error(`Failed rate too hight(${JSON.stringify({ failCount, totalCount })}). Stop verify!!!`);
        return;
      }

      Logger.info("all frames uploaded");
      const requestData: VerifyRequestData = {
        token: self.token
      };
      self.callVerificationApi(requestData, successCallback, errorCallback);
    });
  }

  private callVerificationApi(
    requestData: VerifyRequestData,
    successCallback: (result: boolean, logs: string) => void,
    errorCallback: (error: Error) => void
  ): void {
    const verifyEndpoint: string = Utils.getConfig().API_VERIFY_ENDPOINT_PATTERN.replace(
      "{challengeId}",
      this.challengeId
    );
    const url: string = Utils.getConfig().API_URL + verifyEndpoint;

    Logger.info(`calling ${url}`);
    Logger.info(requestData);
    const promise = axios.post(url, requestData);
    promise
      .then(function(response: any) {
        Logger.info(response);
        const verifyResponseData: VerifyResponseData = response.data;
        Logger.info(verifyResponseData);
        successCallback(verifyResponseData.success, verifyResponseData.logs);
      })
      .catch(function(error: any) {
        Logger.error(error);
        errorCallback(error);
      });
  }

  uploadVideo() {
    try {
      const mediaRecorderInfo: MediaRecorderInfo = (window as any).mediaRecorderInfo;
      const recordedBlob: Blob[] = mediaRecorderInfo.recordedChunks;
      if (recordedBlob.length) {
        const numberOfSecond = parseInt(Utils.getConfig().RECORD_VIDEO_DURATION_IN_SECONDS);
        let videoBlob: Blob[] = recordedBlob;
        if (recordedBlob.length > numberOfSecond) {
          videoBlob = recordedBlob.slice(0, numberOfSecond);
        }
        const videoLocal = new Blob(videoBlob, { type: mediaRecorderInfo.mimeType });
        const reader: FileReader = new FileReader();
        const self = this;
        reader.readAsDataURL(videoLocal);
        reader.onloadend = function() {
          const dataUrl: string = reader.result as string;
          const requestData: VideoRequestData = {
            videoBase64: dataUrl.substr(dataUrl.indexOf(",") + 1),
            token: self.token,
            timestamp: Date.now(),
            extension: mediaRecorderInfo.mimeType
          };
          self.callVideoApi(
            requestData,
            function(response: ResponseData) {
              Logger.info(response);
              Utils.resetMediaRecorder();
            },
            function(error) {
              Logger.error(error);
            }
          );
        };
      }
    } catch (error) {
      EventLogger.singleton.sendUploadVideoErrorEvent(error);
    }
  }

  private callVideoApi(
    requestData: VideoRequestData,
    successCallback: (response: ResponseData) => void,
    errorCallback: (error: any) => void
  ): void {
    const videoEndpoint: string = Utils.getConfig().API_VIDEO_ENDPOINT_PATTERN.replace(
      "{challengeId}",
      this.challengeId
    );
    const url: string = Utils.getConfig().API_URL + videoEndpoint;
    const promise = axios.put(url, requestData);
    promise
      .then(function(response: any) {
        successCallback(response);
      })
      .catch(function(error: any) {
        errorCallback(error);
      });
  }
}
